import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { API, graphqlOperation } from 'aws-amplify';
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from '@awsui/components-react';

import onVPStatusChange from '../../graphql/queries/onVPStatusChange';

const STATUS_DISPLAY = {
  BOOTING: { type: 'pending', label: 'Starting up...' },
  SIGNING_IN: { type: 'in-progress', label: 'Signing in to Zoom...' },
  JOINING: { type: 'in-progress', label: 'Joining meeting...' },
  JOINED: { type: 'success', label: 'In meeting' },
  RECORDING: { type: 'success', label: 'Recording' },
  ENDED: { type: 'stopped', label: 'Meeting ended' },
  FAILED: { type: 'error', label: 'Failed' },
};

const VPStatusPanel = ({ vpId, onDismiss }) => {
  const [vpStatus, setVpStatus] = useState(null);

  useEffect(() => {
    if (!vpId) return undefined;

    const subscription = API.graphql(
      graphqlOperation(onVPStatusChange, { vpId })
    ).subscribe({
      next: ({ value }) => {
        const data = value?.data?.onVPStatusChange;
        if (data) {
          setVpStatus(data);
        }
      },
      error: (err) => {
        console.error('[VPStatusPanel] Subscription error:', err);
      },
    });

    return () => {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
    };
  }, [vpId]);

  if (!vpId) return null;

  const statusInfo = STATUS_DISPLAY[vpStatus?.status] || { type: 'pending', label: 'Connecting...' };
  const isTerminal = vpStatus?.status === 'ENDED' || vpStatus?.status === 'FAILED';

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={
            isTerminal && onDismiss ? (
              <Button onClick={onDismiss} variant="normal">
                Dismiss
              </Button>
            ) : null
          }
        >
          Meeting Assistant Status
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="m">
        <Box>
          <StatusIndicator type={statusInfo.type}>{statusInfo.label}</StatusIndicator>
          {vpStatus?.message && (
            <Box variant="p" color="text-body-secondary" margin={{ top: 'xs' }}>
              {vpStatus.message}
            </Box>
          )}
        </Box>

        {!vpStatus && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
            <Box variant="p" margin={{ top: 's' }}>
              Waiting for assistant to start...
            </Box>
          </Box>
        )}

        {vpStatus?.status === 'FAILED' && (
          <Alert type="error">{vpStatus.message || 'The meeting assistant encountered an error.'}</Alert>
        )}
      </SpaceBetween>
    </Container>
  );
};

VPStatusPanel.propTypes = {
  vpId: PropTypes.string.isRequired,
  onDismiss: PropTypes.func,
};

VPStatusPanel.defaultProps = {
  onDismiss: null,
};

export default VPStatusPanel;
